import {
  classifyPanelFetchThrow,
  classifyPanelHttpErrorResponse,
  devPanelLoginLog,
  interpretXtreamLoginResponse,
  normalizePanelResponseText,
} from '../utils/iptvPanelErrors';
import type {
  CatalogSnapshot,
  CategoryItem,
  EpisodeItem,
  LiveChannel,
  SeriesDetail,
  SeriesItem,
  VodItem,
  XtreamCategoryRow,
  XtreamEpisodeRow,
  XtreamLiveStreamRow,
  XtreamSeriesInfoResponse,
  XtreamSeriesRow,
  XtreamUserInfoResponse,
  XtreamVodStreamRow,
} from '../types';

function trimBase(url: string): string {
  return url.replace(/\/+$/, '');
}

function encodeQP(u: string, p: string, action: string, extra?: Record<string, string>): string {
  const params = new URLSearchParams({
    username: u,
    password: p,
    action,
    ...extra,
  });
  return params.toString();
}

async function fetchXtreamJson<T>(
  baseUrl: string,
  username: string,
  password: string,
  action: string,
  extra?: Record<string, string>,
): Promise<T> {
  const q = encodeQP(username, password, action, extra);
  const url = `${trimBase(baseUrl)}/player_api.php?${q}`;
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET' });
  } catch (e) {
    const msg = classifyPanelFetchThrow(e);
    devPanelLoginLog({
      url,
      classification: 'fetch_throw',
      snippet: e instanceof Error ? e.message : String(e),
      note: msg,
    });
    throw new Error(msg);
  }
  const rawBody = await res.text();
  const bodyText = normalizePanelResponseText(rawBody);
  if (!res.ok) {
    const msg = classifyPanelHttpErrorResponse(res.status, bodyText);
    devPanelLoginLog({
      url,
      status: res.status,
      snippet: bodyText.slice(0, 500),
      classification: `http_${res.status}`,
      note: msg,
    });
    throw new Error(msg);
  }
  if (!bodyText.startsWith('{') && !bodyText.startsWith('[')) {
    const msg = classifyPanelHttpErrorResponse(res.status, bodyText);
    devPanelLoginLog({
      url,
      status: res.status,
      snippet: bodyText.slice(0, 500),
      classification: 'non_json_body',
      note: msg,
    });
    throw new Error(msg);
  }
  try {
    return JSON.parse(bodyText) as T;
  } catch {
    const msg = classifyPanelHttpErrorResponse(res.status, bodyText);
    devPanelLoginLog({
      url,
      status: res.status,
      snippet: bodyText.slice(0, 500),
      classification: 'json_parse_error',
      note: msg,
    });
    throw new Error(msg);
  }
}

export async function xtreamLogin(
  baseUrl: string,
  username: string,
  password: string,
): Promise<XtreamUserInfoResponse> {
  const q = new URLSearchParams({ username, password });
  const url = `${trimBase(baseUrl)}/player_api.php?${q.toString()}`;
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET' });
  } catch (e) {
    const msg = classifyPanelFetchThrow(e);
    devPanelLoginLog({
      url,
      classification: 'fetch_throw',
      snippet: e instanceof Error ? e.message : String(e),
      note: msg,
    });
    throw new Error(msg);
  }
  const bodyText = await res.text();
  const interpreted = interpretXtreamLoginResponse(res.status, bodyText, url);
  if (!interpreted.ok) {
    throw new Error(interpreted.userMessage);
  }
  return interpreted.data;
}

function buildPlayBase(baseUrl: string, info: XtreamUserInfoResponse): string {
  const si = info.server_info;
  if (!si?.url) return trimBase(baseUrl);
  const proto = (si.server_protocol || 'http').replace(/:$/, '');
  const port = si.https_port && proto === 'https' ? si.https_port : si.port;
  const host = si.url.replace(/^https?:\/\//, '').split('/')[0];
  return `${proto}://${host}${port ? `:${port}` : ''}`;
}

function mapCategories(rows: XtreamCategoryRow[] | undefined): CategoryItem[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    id: String(r.category_id ?? r.id ?? ''),
    name: String(r.category_name ?? r.name ?? 'Unnamed'),
  })).filter((c) => c.id);
}

/** Xtream live: browsers need HLS (.m3u8); raw .ts often fails in the video element. */
function normalizeLiveContainerExt(raw: string | undefined | null): string {
  if (raw == null || String(raw).trim() === '') return 'm3u8';
  let e = String(raw).replace(/^\./, '').toLowerCase();
  if (e === 'hls') e = 'm3u8';
  return e;
}

function normalizeVodContainerExt(raw: string | undefined | null): string {
  if (raw == null || String(raw).trim() === '') return 'mp4';
  return String(raw).replace(/^\./, '').toLowerCase();
}

/** Yield to the browser between chunks so huge playlists don’t freeze the tab. */
async function mapInChunks<T, R>(items: T[], chunkSize: number, mapFn: (item: T) => R): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    for (const item of chunk) {
      out.push(mapFn(item));
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 0);
    });
  }
  return out;
}

export function buildXtreamLiveStreamUrl(
  playBase: string,
  username: string,
  password: string,
  streamId: string,
  ext: string = 'ts',
): string {
  return `${trimBase(playBase)}/live/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${streamId}.${ext}`;
}

export function buildXtreamVodUrl(
  playBase: string,
  username: string,
  password: string,
  streamId: string,
  ext: string = 'mp4',
): string {
  return `${trimBase(playBase)}/movie/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${streamId}.${ext}`;
}

export function buildXtreamSeriesUrl(
  playBase: string,
  username: string,
  password: string,
  streamId: string,
  ext: string = 'mp4',
): string {
  return `${trimBase(playBase)}/series/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${streamId}.${ext}`;
}

export async function loadXtreamCatalog(
  baseUrl: string,
  username: string,
  password: string,
  sourceId: string,
): Promise<CatalogSnapshot> {
  const login = await xtreamLogin(baseUrl, username, password);
  const playBase = buildPlayBase(baseUrl, login);

  const [liveCat, vodCat, seriesCat, liveStreams, vodStreams, seriesList] = await Promise.all([
    fetchXtreamJson<XtreamCategoryRow[]>(baseUrl, username, password, 'get_live_categories'),
    fetchXtreamJson<XtreamCategoryRow[]>(baseUrl, username, password, 'get_vod_categories'),
    fetchXtreamJson<XtreamCategoryRow[]>(baseUrl, username, password, 'get_series_categories'),
    fetchXtreamJson<XtreamLiveStreamRow[]>(baseUrl, username, password, 'get_live_streams'),
    fetchXtreamJson<XtreamVodStreamRow[]>(baseUrl, username, password, 'get_vod_streams'),
    fetchXtreamJson<XtreamSeriesRow[]>(baseUrl, username, password, 'get_series'),
  ]);

  const liveCategories = mapCategories(liveCat);
  const vodCategories = mapCategories(vodCat);
  const seriesCategories = mapCategories(seriesCat);

  const liveRows = Array.isArray(liveStreams) ? liveStreams : [];
  const liveChannels: LiveChannel[] = await mapInChunks(liveRows, 2500, (s) => {
    const sid = String(s.stream_id ?? '');
    const ext = normalizeLiveContainerExt(s.container_extension);
    return {
      id: sid,
      categoryId: String(s.category_id ?? ''),
      name: String(s.name ?? 'Channel'),
      logo: s.stream_icon || undefined,
      streamId: sid,
      containerExtension: ext,
      streamUrl: sid
        ? buildXtreamLiveStreamUrl(playBase, username, password, sid, ext)
        : undefined,
    };
  });

  const vodRows = Array.isArray(vodStreams) ? vodStreams : [];
  const vodItems: VodItem[] = await mapInChunks(vodRows, 2500, (v) => {
    const vid = String(v.stream_id ?? '');
    const ext = normalizeVodContainerExt(v.container_extension);
    return {
      id: vid,
      categoryId: String(v.category_id ?? ''),
      name: String(v.name ?? 'Movie'),
      poster: v.stream_icon || undefined,
      year: v.year ? String(v.year) : undefined,
      genre: v.genre || undefined,
      containerExtension: ext,
      streamUrl: vid
        ? buildXtreamVodUrl(playBase, username, password, vid, ext)
        : undefined,
    };
  });

  const seriesRows = Array.isArray(seriesList) ? seriesList : [];
  const seriesItems: SeriesItem[] = await mapInChunks(seriesRows, 4000, (s) => ({
    id: String(s.series_id ?? ''),
    categoryId: String(s.category_id ?? ''),
    name: String(s.name ?? 'Series'),
    poster: s.cover || undefined,
    year: s.year ? String(s.year) : undefined,
    genre: s.genre || undefined,
  }));

  return {
    sourceId,
    liveCategories,
    liveChannels,
    vodCategories,
    vodItems,
    seriesCategories,
    seriesItems,
    seriesDetails: {},
  };
}

export async function loadXtreamSeriesDetail(
  baseUrl: string,
  username: string,
  password: string,
  series: SeriesItem,
  playBase: string,
): Promise<SeriesDetail> {
  const data = await fetchXtreamJson<XtreamSeriesInfoResponse>(
    baseUrl,
    username,
    password,
    'get_series_info',
    { series_id: series.id },
  );
  const episodesMap = data.episodes && typeof data.episodes === 'object' ? data.episodes : {};
  const seasonMap = new Map<number, EpisodeItem[]>();

  Object.keys(episodesMap).forEach((seasonKey) => {
    const eps = episodesMap[seasonKey];
    if (!Array.isArray(eps)) return;
    const sn = Number(seasonKey);
    eps.forEach((ep: XtreamEpisodeRow) => {
      const eid = String(ep.id ?? '');
      const ext = ep.container_extension || 'mp4';
      const epNum = Number(ep.episode_num ?? 0);
      const seasonNum = Number(ep.season ?? sn);
      const item: EpisodeItem = {
        id: eid,
        seasonNumber: seasonNum,
        episodeNumber: epNum,
        title: String(ep.title ?? `Episode ${epNum}`),
        containerExtension: ext,
        streamUrl: eid
          ? buildXtreamSeriesUrl(playBase, username, password, eid, ext)
          : undefined,
      };
      if (!seasonMap.has(seasonNum)) seasonMap.set(seasonNum, []);
      seasonMap.get(seasonNum)!.push(item);
    });
  });

  const seasons = Array.from(seasonMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([seasonNumber, episodes]) => ({
      seasonNumber,
      episodes: episodes.sort((x, y) => x.episodeNumber - y.episodeNumber),
    }));

  return { series, seasons };
}

/** Re-resolve play base from stored server + optional cached login shape */
export function playBaseFromLogin(baseUrl: string, login: XtreamUserInfoResponse): string {
  return buildPlayBase(baseUrl, login);
}
